<template>
	<div v-if="variable != undefined">
		<!-- ID -->
		<div class="data-item" v-if="variable.id">
			<div class="label">
				<strong>{{ $t("id") }}</strong>
			</div>
			<div class="value" style="width: 350px">
				<el-input v-model="variable.id" :disabled="true" style="width: 100%" />
			</div>
		</div>

		<!-- NOME -->
		<div class="data-item">
			<div class="label">
				<strong>{{ $t("name") }}</strong>
			</div>
			<div class="value" style="width: 350px" v-if="editable">
				<el-input
					v-model="variable.name"
					:disabled="variable.rules.protected_name"
					:placeholder="$t('name')"
					@input="checkUnique"
					:class="{ error: error && error.indexOf('name') > -1 }"
					style="width: 100%"
				>
					<i class="el-icon-error el-input__icon" v-if="uniqueError" style="color: red" slot="suffix" />
				</el-input>
			</div>
			<div v-else class="value" style="padding: 7px">
				<span>{{ variable.name }}</span>
			</div>
		</div>

		<div class="data-item" v-if="uniqueError">
			<div class="label"></div>
			<span style="color: red">
				{{ variable.name == "" ? $t("required-field") : $t("unique-name-variable") }}
			</span>
		</div>

		<!-- LIVELLO -->
		<div class="data-item">
			<div class="label">
				<strong>{{ $t("level") }}</strong>
			</div>
			<div class="value" style="width: 350px">
				<div style="display: flex">
					<el-select
						v-model="variable.level"
						style="width: 100%"
						filterable
						value-key="id"
						:placeholder="$t('level')"
						@change="() => $emit('change', 'level')"
						:disabled="variable.rules.protected_name"
					>
						<el-option
							v-for="level of levels"
							:key="level.level"
							:label="level.name"
							:value="level.level"
						/>
					</el-select>
				</div>
			</div>
		</div>

		<!-- TIPOLOGIA -->
		<div class="data-item">
			<div class="label">
				<strong>{{ $t("type") }}</strong>
			</div>
			<div class="value" style="width: 350px">
				<div style="display: flex">
					<el-select
						v-model="variable.type"
						style="width: 100%"
						filterable
						value-key="id"
						:placeholder="$t('type')"
						@change="() => $emit('change', 'type')"
						:disabled="variable.rules.protected_name"
					>
						<el-option v-for="type of types" :key="type" :label="$t(type)" :value="type" />
					</el-select>
				</div>
			</div>
		</div>

		<!-- ordinamento -->
		<div class="data-item" v-if="variable.level < 1">
			<div class="label">
				<strong>{{ $t("order") }}</strong>
			</div>
			<div class="value" style="width: 350px">
				<div style="display: flex">
					<el-input-number
						v-model="variable.order"
						style="width: 100%"
						@change="() => $emit('change', 'order')"
					>
					</el-input-number>
				</div>
			</div>
		</div>
		<!-- RULES -->
		<div v-if="variable.type != undefined">
			<el-divider style="width: 80%" />

			<!-- RULES nullable-->
			<div class="data-item" style="margin-top: 20px" v-if="type_rules[variable.type].includes('nullable')">
				<div class="label">
					<strong>{{ $t("nullable") }}</strong>
				</div>
				<div class="value" style="width: 235px">
					<el-switch
						v-model="variable.rules.nullable"
						active-color="#13ce66"
						@change="() => $emit('change', 'nullable')"
					/>
				</div>
			</div>

			<!-- RULES placeholder-->
			<div class="data-item" v-if="type_rules[variable.type].includes('placeholder')">
				<div class="label">
					<strong>{{ $t("placeholder") }}</strong>
				</div>
				<div class="value" style="width: 350px">
					<el-input
						v-model="variable.rules.placeholder"
						clearable
						style="width: 100%"
						@input="() => $emit('change', 'placeholder')"
						:placeholder="variable.name"
					/>
				</div>
			</div>

			<!-- RULES tip-->
			<div class="data-item" v-if="type_rules[variable.type].includes('tip')">
				<div class="label">
					<strong>{{ $t("tip") }}</strong>
				</div>
				<div class="value" style="width: 350px">
					<el-input
						v-model="variable.rules.tip"
						clearable
						style="width: 100%"
						@input="() => $emit('change', 'tip')"
					/>
				</div>
			</div>

			<!-- RULES readonly-->
			<div class="data-item" style="margin-top: 20px" v-if="type_rules[variable.type].includes('readonly')">
				<div class="label">
					<strong>{{ $t("readonly") }}</strong>
				</div>
				<div class="value" style="width: 235px">
					<el-switch
						@change="
							() => {
								$emit('change', 'readonly')
							}
						"
						v-model="variable.rules.readonly"
						active-color="#13ce66"
					/>
				</div>
			</div>

			<!-- RULES default-->
			<div class="data-item" v-if="type_rules[variable.type].includes('readonly') && variable.rules.readonly">
				<div class="label">
					<strong>{{ $t("default-value") }}</strong>
				</div>
				<div class="value" style="width: 350px">
					<el-input
						v-model="variable.rules.default"
						clearable
						style="width: 100%"
						@input="() => $emit('change', 'default')"
					/>
				</div>
			</div>

			<!-- RULES computed_bool-->
			<div class="data-item" style="margin-top: 20px" v-if="type_rules[variable.type].includes('computed_bool')">
				<div class="label">
					<strong>{{ $t("computed_bool") }}</strong>
				</div>
				<div class="value" style="width: 235px">
					<el-switch
						@change="
							() => {
								computedSwitch()
								$emit('change', 'computed_bool')
							}
						"
						v-model="variable.rules.computed_bool"
						active-color="#13ce66"
					/>
				</div>
			</div>

			<!-- RULES computed_rules -->
			<div v-if="type_rules[variable.type].includes('computed_rules') && variable.rules.computed_bool">
				<div class="data-item" style="flex-wrap: wrap" v-for="(_, index) in cascaderValues" :key="index">
					<div class="label">
						<strong v-if="index == 0">{{ $t("computed_rules") }}</strong>
					</div>
					<div>
						<el-cascader
							v-model="cascaderValues[index]"
							:options="cascaderOptions"
							@change="
								value => {
									cascaderHandleChange(value, index)
								}
							"
						/>
						<el-button
							icon="el-icon-plus"
							circle
							size="small"
							style="margin-left: 5px"
							@click="addRule(index)"
						/>
						<el-button
							icon="el-icon-delete"
							circle
							size="small"
							style="margin-left: 5px"
							@click="deleteRule(index)"
						/>
					</div>
				</div>
			</div>

			<!-- RULES step -->
			<div class="data-item" v-if="type_rules[variable.type].includes('step')">
				<div class="label">
					<strong>{{ $t("step") }}</strong>
				</div>
				<div class="value" style="width: 350px">
					<div style="display: flex">
						<el-select
							v-model="variable.rules.step"
							style="width: 100%"
							filterable
							value-key="id"
							:placeholder="$t('step')"
							@change="() => $emit('change', 'step')"
							clearable
						>
							<el-option v-for="step of steps" :key="step.step" :label="step.name" :value="step.step" />
						</el-select>
					</div>
				</div>
			</div>

			<!-- RULES min -->
			<div class="data-item" v-if="type_rules[variable.type].includes('min') && !variable.rules.computed_bool">
				<div class="label">
					<strong>{{ $t("min") }}</strong>
				</div>
				<div class="value" style="width: 350px">
					<el-input-number
						v-model="variable.rules.min"
						:step="variable.rules.step ? variable.rules.step : 1"
						:precision="precision"
						:max="variable.rules.max"
						@change="() => $emit('change', 'min')"
					/>
				</div>
			</div>

			<!-- RULES max -->
			<div class="data-item" v-if="type_rules[variable.type].includes('max') && !variable.rules.computed_bool">
				<div class="label">
					<strong>{{ $t("max") }}</strong>
				</div>
				<div class="value" style="width: 350px">
					<el-input-number
						v-model="variable.rules.max"
						:step="variable.rules.step ? variable.rules.step : 1"
						:precision="precision"
						:min="variable.rules.min"
						@change="() => $emit('change', 'max')"
					/>
				</div>
			</div>

			<!-- RULES list -->
			<div class="data-item" v-if="type_rules[variable.type].includes('list') && !variable.rules.computed_bool">
				<div class="label">
					<strong>{{ $t("list") }}</strong>
				</div>
				<div>
					<el-tag
						v-for="tag in listaVariabili"
						:key="tag"
						closable
						:disable-transitions="false"
						@close="handleClose(tag)"
						style="margin-top: 5px"
					>
						{{ tag }}
					</el-tag>

					<el-input
						clearable
						class="input-new-tag"
						v-if="newTagVisible"
						v-model="newTagValue"
						ref="saveTagInput"
						size="mini"
						@keyup.enter.native="insertNewTag"
						@blur="insertNewTag"
						style="margin-top: 5px"
					/>
					<el-button v-else class="button-new-tag" size="small" @click="newTag" ref="newtag">
						{{ $t("new-tag") }}
					</el-button>
				</div>
			</div>

			<!-- RULES height-->
			<div class="data-item" v-if="type_rules[variable.type].includes('height')">
				<div class="label">
					<strong>{{ $t("height") }} (px)</strong>
				</div>
				<div class="value" style="width: 350px">
					<el-input-number
						v-model="variable.rules.height"
						clearable
						style="width: 100%"
						@input="() => $emit('change', 'height')"
					/>
				</div>
			</div>

			<!-- RULES width-->
			<div class="data-item" v-if="type_rules[variable.type].includes('width')">
				<div class="label">
					<strong>{{ $t("width") }} (px)</strong>
				</div>
				<div class="value" style="width: 350px">
					<el-input-number
						v-model="variable.rules.width"
						clearable
						style="width: 100%"
						@input="() => $emit('change', 'width')"
					/>
				</div>
			</div>
		</div>

		<div v-if="variable.id != undefined">
			<el-divider style="width: 80%" />
			<div class="data-item">
				<div class="label">
					<strong>{{ $t("active-from") }}</strong>
				</div>
				<div class="value">
					<span>
						{{ moment(variable.created_at).format("DD/MM/YYYY HH:mm") }}
					</span>
				</div>
			</div>
			<div class="data-item" v-if="variable.updated_at != '0001-01-01T00:00:00Z'">
				<div class="label">
					<strong>{{ $t("last-update") }}</strong>
				</div>
				<div class="value">
					<span>
						{{ moment(variable.updated_at).format("DD/MM/YYYY HH:mm") }}
					</span>
				</div>
			</div>
			<div class="data-item" v-if="variable.deleted_at != undefined">
				<div class="label">
					<strong>{{ $t("deleted-at") }}</strong>
				</div>
				<div class="value">
					<span>
						{{ moment(variable.deleted_at).format("DD/MM/YYYY HH:mm") }}
					</span>
				</div>
			</div>
		</div>

		<div v-if="variable.type != undefined">
			<el-divider style="width: 80%" />
			<div class="data-item" style="margin-top: 20px">
				<div class="label">
					<strong style="font-size: 2em">{{ $t("example") }}</strong>
				</div>
			</div>
			<div class="data-item" style="margin-top: 20px">
				<div class="label">
					<strong>{{ variable.name }}</strong>
				</div>
				<div class="value">
					<field-type level="0" :ownerId="'1'" :field="variable" :overrideSuperAdmin="true" />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from "moment"
import axios from "axios"
import { mapActions, mapGetters } from "vuex"

import FieldType from "@/modules/organization/form-component/field-type"

export default {
	name: "variables-form",
	components: { FieldType },
	props: ["variable", "error", "editable", "source", "reset"],
	data() {
		return {
			uniqueError: false,
			types: ["text", "long-text", "number", "choice", "image", "date"],
			levels: [
				{ level: -1, name: this.$t("required-tag") },
				{ level: 0, name: this.$t("global-tag") },
				{ level: 1, name: this.$t("project-tag") },
				{ level: 2, name: this.$t("procedure-tag") },
				{ level: 3, name: this.$t("document-tag") }
			],
			steps: [
				{ step: 1, name: "0 " + this.$t("step") },
				{ step: 0.1, name: "1 " + this.$t("decimal") },
				{ step: 0.01, name: "2 " + this.$t("step") },
				{ step: 0.001, name: "3 " + this.$t("step") }
			],
			type_rules: {
				text: ["nullable", "placeholder", "tip", "computed_bool", "computed_rules", "readonly", "min", "max"],
				"long-text": [
					"nullable",
					"placeholder",
					"tip",
					"computed_bool",
					"computed_rules",
					"readonly",
					"min",
					"max"
				],
				image: ["nullable", "tip", "height", "width"],
				date: ["nullable", "placeholder", "tip"],
				choice: ["nullable", "placeholder", "tip", "list"],
				number: ["nullable", "tip", "readonly", "min", "max", "step"]
			},
			newTagVisible: false,
			newTagValue: "",

			cascaderValues: [[]],
			cascaderOptions: [
				{
					value: "val",
					label: "Valore"
				},
				{
					value: "settings",
					label: "Impostazioni",
					children: [
						// fillCascaderOptions
					]
				},
				{
					value: "variables",
					label: "Variabili",
					children: [
						{
							value: "obbligatorie",
							label: "Obbligatorie",
							children: [
								// fillCascaderOptions
							]
						},
						{
							value: "globali",
							label: "Globali",
							children: [
								// fillCascaderOptions
							]
						},
						{
							value: "progetto",
							label: "Progetto",
							children: [
								// fillCascaderOptions
							]
						},
						{
							value: "procedura",
							label: "Procedura",
							children: [
								// fillCascaderOptions
							]
						},
						{
							value: "documento",
							label: "Documento",
							children: [
								// fillCascaderOptions
							]
						}
					]
				},
				{
					value: "char",
					label: "Caratteri",
					children: [
						{
							value: "dot",
							label: "Punto"
						},
						{
							value: "space",
							label: "Spazio"
						},
						{
							value: "dash",
							label: "Trattino"
						},
						{
							value: "slash",
							label: "Barra"
						},
						{
							value: "comma",
							label: "Virgola"
						}
					]
				},
				{
					value: "func",
					label: "Funzioni",
					children: [
						{
							value: "current_date",
							label: "data odierna",
							children: [
								{
									value: "DD/MM/YYYY",
									label: "gg/mm/aaaa"
								},
								{
									value: "YYYY",
									label: "aaaa"
								}
							]
						},
						{
							value: "upper_case",
							label: "tutto maiuscolo"
						}
					]
				}
			]
		}
	},
	computed: {
		listaVariabili() {
			return this.variable.rules.list
		},
		...mapGetters({
			settings: "modules/settings",
			variables: "modules/variables"
		}),
		precision() {
			var precision = 0
			if (this.variable.rules.step) {
				switch (this.variable.rules.step) {
					case 1:
						precision = 0
						break
					case 0.1:
						precision = 1
						break
					case 0.01:
						precision = 2
						break
					case 0.001:
						precision = 3
						break
					default:
						precision = undefined
				}
			}

			return precision
		}
	},
	async created() {
		if (this.variables.every(variableArray => variableArray.length === 0)) await this.doVariables()
		if (Object.keys(this.settings).length === 0) await this.doSettings()
		this.fillCascaderOptions()
		if (this.variable.custom_field_value == null)
			this.variable.custom_field_value = { value: this.getCascaderVal() }
		if (this.variable.rules && !this.variable.rules.list) this.variable.rules.list = []
		if (this.variable.rules && this.variable.rules.computed_rules)
			this.cascaderValues = this.variable.rules.computed_rules
	},
	watch: {
		variable: {
			handler(val, oldVal) {
				if (val.rules.computed_rules != oldVal.rules.computed_rules) {
					if (val.rules.computed_rules) this.cascaderValues = val.rules.computed_rules
					else this.cascaderValues = [[]]
				}
				this.variable.custom_field_value.og = this.getCascaderVal()
			},
			deep: true
		},
		cascaderValues: {
			handler(val) {
				this.variable.rules.computed_rules = val
			},
			deep: true
		},
		reset: {
			handler(val) {
				if (val) {
					// se lo metto in un oggetto (this.variable = {tutta la roba}) dà errore
					// di avoid mutating
					// non so se sia il metodo più elegante per farlo ma così viene resettato il form
					this.variable.name = undefined
					this.variable.level = undefined
					this.variable.type = undefined
					this.variable.rules = {}
					this.variable.custom_field_value = {}
				}
			},
			deep: true,
			immediate: true
		}
	},
	methods: {
		moment,
		...mapActions({
			doVariables: "modules/doVariables",
			doSettings: "modules/doSettings"
		}),
		computedSwitch: function () {
			this.variable.custom_field_value.value = this.getCascaderVal()
		},
		handleClose(tag) {
			this.variable.rules.list.splice(this.variable.rules.list.indexOf(tag), 1)

			this.$emit("change", "close")
		},
		newTag() {
			this.newTagVisible = true
			this.$nextTick(_ => {
				this.$refs.saveTagInput.$refs.input.focus()
			})
		},
		insertNewTag() {
			let newTagValue = this.newTagValue
			if (newTagValue) {
				if (!this.variable.rules.list.includes(newTagValue)) {
					this.variable.rules.list.push(newTagValue)
					this.$emit("change", "newTag")
					this.newTagValue = ""
				} else {
					this.newTagValue = ""
					this.$notification("error", this.$t("cant-repeat-value"), "", 0)
				}
			} else {
				this.newTagVisible = false
				this.newTagValue = ""
			}
		},
		cascaderHandleChange(value, index) {
			this.$emit("change", "cascader change")
		},
		deleteRule(index) {
			this.cascaderValues.splice(index, 1)
			if (!this.cascaderValues.length) this.cascaderValues = [[]]
			this.$emit("change", "cascader delete")
		},
		addRule(index) {
			this.cascaderValues.splice(index + 1, 0, [])
			this.$emit("change", "cascader add")
		},
		checkUnique() {
			this.$emit("change", "checkUnique")
			if (this.variable.name == "") {
				this.uniqueError = true
				return
			}
			const payload = {
				levels: [-1, 0, 1, 2, 3],
				filter: this.variable.name,
				exact: true
			}
			axios
				.post(process.env.VUE_APP_API_URL + "/api/customfields/filters", payload, {
					headers: { Authorization: "Bearer " + localStorage.getItem("JWT") }
				})
				.then(res => {
					if (res.data.array.length) {
						if (res.data.array[0].id !== this.variable.id) {
							this.uniqueError = true
							this.$emit("uniqueName", this.uniqueError)
							return
						}
					}
					this.uniqueError = false
					this.$emit("uniqueName", this.uniqueError)
				})
				.catch(err => console.error(err))
		},
		fillCascaderOptions() {
			this.cascaderOptions[1].children = Object.keys(this.settings || {}).map(key => ({
				value: key,
				label: this.$t(key)
			}))

			const children = this.variables.map(array =>
				array.map(variable => ({ value: variable.id, label: variable.name }))
			)

			children.forEach((child, index) => (this.cascaderOptions[2].children[index].children = child))
		},
		getCascaderVal() {
			let returnValue = ""
			if (this.variable.rules.computed_bool)
				returnValue =
					this.variable.rules.readonly && this.variable.rules.default != ""
						? this.variable.rules.default
						: "valore"

			return returnValue
		}
	}
}
</script>
<style scoped lang="scss">
@import "src/assets/scss/my-form";

.error {
	border: 1px solid red;
}

.el-tag + .el-tag {
	margin-left: 10px;
}

.button-new-tag {
	margin-left: 10px;
	height: 32px;
	line-height: 30px;
	padding-top: 0;
	padding-bottom: 0;
}

.input-new-tag {
	width: 90px;
	margin-left: 10px;
	vertical-align: bottom;
}
</style>
